import { Controller } from "stimulus";

import { PhoneStatusMenu } from "../phone_dashboard/phone_status_menu";
import { showToast } from "../utils/toast";

const ROW_SELECTOR = "table.grid.phone-dashboard tbody tr[data-bp-id]";
const DEFAULT_BUCKET = "inbox";
const THUMB_LABELS: Record<string, string> = {
  short_list: "Phone short list",
  do_not_call: "Don’t call",
};
const LEAVE_MS = 200;

const parseCount = (text: string | null): number => Number((text || "").replace(/[^\d]/g, "")) || 0;
const formatCount = (count: number): string => count.toLocaleString("en-US");

export default class extends Controller {
  static targets = ["bar", "selected", "count", "empty"];

  static values = {
    url: String,
    bucket: String,
    focus: String,
    statusCounts: Object,
  };

  declare readonly hasBarTarget: boolean;
  declare readonly barTarget: HTMLElement;
  declare readonly hasSelectedTarget: boolean;
  declare readonly selectedTarget: HTMLElement;
  declare readonly countTargets: HTMLElement[];
  declare readonly hasEmptyTarget: boolean;
  declare readonly emptyTarget: HTMLElement;

  declare readonly urlValue: string;
  declare readonly bucketValue: string;
  declare readonly focusValue: string;
  declare readonly statusCountsValue: object;

  private busy = false;
  private statusMenu: PhoneStatusMenu | null = null;

  connect(): void {
    this.fillStatusCounts();
    this.selectFocused();
    this.syncSelection();
    const menu = this.element.querySelector<HTMLElement>("[data-phone-status-menu]");
    if (menu) this.statusMenu = new PhoneStatusMenu(menu);
  }

  // The Status dropdown lives outside the async table, so its counts arrive with the rows.
  private fillStatusCounts(): void {
    const counts = this.statusCountsValue as Record<string, Record<string, number>>;
    document.querySelectorAll<HTMLElement>("[data-status-count]").forEach((el) => {
      const [section, value] = (el.dataset.statusCount || "").split(":");
      const count = counts[section]?.[value];
      el.textContent = count === undefined ? "" : String(count);
      el.hidden = count === undefined;
    });
  }

  disconnect(): void {
    this.statusMenu?.destroy();
    this.statusMenu = null;
  }

  // The selected rows' ids, in on-screen order: the call session's queue.
  selectedIds(): string[] {
    return this.selectedRows().map((row) => row.dataset.bpId || "");
  }

  click(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (!target) return;

    const phoneStatus = target.closest<HTMLElement>("[data-phone-status]");
    if (phoneStatus) {
      this.statusMenu?.toggle(phoneStatus);
      return;
    }

    const thumb = target.closest<HTMLElement>("[data-tri]");
    if (thumb) {
      const row = thumb.closest<HTMLTableRowElement>("tr[data-bp-id]");
      const chosen = thumb.dataset.tri || DEFAULT_BUCKET;
      // pressing the active thumb puts the row back to triage
      if (row) this.triage([row], row.dataset.bucket === chosen ? DEFAULT_BUCKET : chosen);
      return;
    }

    const bulk = target.closest<HTMLElement>("[data-bulk-tri]");
    if (bulk) {
      const rows = this.selectedRows();
      if (rows.length) this.triage(rows, bulk.dataset.bulkTri || DEFAULT_BUCKET, true);
      return;
    }

    if (target.closest("[data-sel-all]")) {
      this.toggleAll();
      return;
    }

    const box = target.closest<HTMLElement>("[data-sel]");
    if (box) {
      const row = box.closest<HTMLTableRowElement>("tr");
      if (row) this.setSelected(row, !row.classList.contains("sel"));
      this.syncSelection();
      return;
    }

    if (target.closest("[data-sel-clear]")) this.clearSelection();

    if (target.closest("[data-call-session-start]")) {
      const ids = this.selectedIds();
      // each id's campaign, in the same order, for the admin-only client brief
      const botIds = this.selectedRows().map((row) => row.dataset.campaign || "");
      if (ids.length) window.dispatchEvent(new CustomEvent("call-session:open", { detail: { ids, botIds } }));
      return;
    }

    const pill = target.closest<HTMLElement>(".st-next .nt");
    if (pill) {
      this.openTaskDialog(pill, "edit");
      return;
    }

    const add = target.closest<HTMLElement>("[data-nt-add]");
    if (add) this.openTaskDialog(add, "new");
  }

  // Opened from To-do's Call button: that candidate's row starts selected, ready for a session.
  private selectFocused(): void {
    if (!this.focusValue) return;

    this.element.querySelectorAll<HTMLTableRowElement>(ROW_SELECTOR).forEach((row) => {
      if (row.dataset.bpId === this.focusValue) this.setSelected(row, true);
    });
  }

  // The dialog is a page-level controller; the grid only says which candidate and which mode.
  private openTaskDialog(origin: HTMLElement, mode: "new" | "edit"): void {
    const row = origin.closest<HTMLTableRowElement>("tr[data-bp-id]");
    if (!row) return;

    const d = origin.dataset;
    window.dispatchEvent(new CustomEvent("call-task-dialog:open", {
      detail: {
        mode,
        candidateId: row.dataset.bpId,
        candidateName: row.dataset.fName,
        task: mode === "edit"
          ? {
              id: d.taskId,
              title: d.title,
              date: d.date,
              time: d.time,
              who: d.who,
              whoEmail: d.whoEmail,
              whoInitials: d.whoInitials,
              note: d.note,
              recording: d.rec
            }
          : undefined
      }
    }));
  }

  // A call outcome was logged for this candidate: the row leaves the selection (§3.3).
  deselect(event: CustomEvent<{ id: string }>): void {
    const id = event.detail?.id;
    if (!id) return;

    this.rows().filter((row) => row.dataset.bpId === id).forEach((row) => this.setSelected(row, false));
    this.syncSelection();
  }

  // The checkboxes are the design system's `.check` spans: Space and Enter toggle them.
  keydown(event: KeyboardEvent): void {
    if (event.key !== " " && event.key !== "Enter") return;

    const box = (event.target as HTMLElement | null)?.closest<HTMLElement>("[data-sel], [data-sel-all]");
    if (!box) return;

    event.preventDefault();
    box.click();
  }

  private rows(): HTMLTableRowElement[] {
    return Array.from(this.element.querySelectorAll<HTMLTableRowElement>(ROW_SELECTOR));
  }

  // Rows the client-side advanced filter hides are not "on the page".
  private visibleRows(): HTMLTableRowElement[] {
    return this.rows().filter((row) => row.getClientRects().length > 0);
  }

  private selectedRows(): HTMLTableRowElement[] {
    return this.rows().filter((row) => row.classList.contains("sel"));
  }

  private setSelected(row: HTMLTableRowElement, on: boolean): void {
    row.classList.toggle("sel", on);
    const box = row.querySelector<HTMLElement>("[data-sel]");
    if (!box) return;

    box.classList.toggle("on", on);
    box.setAttribute("aria-checked", String(on));
  }

  private toggleAll(): void {
    const rows = this.visibleRows();
    const select = !rows.every((row) => row.classList.contains("sel"));
    rows.forEach((row) => this.setSelected(row, select));
    this.syncSelection();
  }

  private clearSelection(): void {
    this.selectedRows().forEach((row) => this.setSelected(row, false));
    this.syncSelection();
  }

  // The bar's count and visibility, and the header box: on, part (some) or off.
  private syncSelection(): void {
    const count = this.selectedRows().length;
    if (this.hasBarTarget) this.barTarget.hidden = count === 0;
    if (this.hasSelectedTarget) this.selectedTarget.textContent = String(count);

    const all = this.element.querySelector<HTMLElement>("[data-sel-all]");
    if (!all) return;

    const visible = this.visibleRows();
    const every = visible.length > 0 && visible.every((row) => row.classList.contains("sel"));
    const some = !every && visible.some((row) => row.classList.contains("sel"));
    all.classList.toggle("on", every);
    all.classList.toggle("part", some);
    all.setAttribute("aria-checked", every ? "true" : some ? "mixed" : "false");
  }

  private async triage(rows: HTMLTableRowElement[], bucket: string, bulk = false): Promise<void> {
    if (this.busy) return;
    this.busy = true;

    try {
      const response = await fetch(this.urlValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || "",
        },
        body: JSON.stringify({ bots_prospect_ids: rows.map((row) => row.dataset.bpId), bucket }),
      });
      const data = await response.json().catch(() => null);
      if (!response.ok || !data || !data.ok) throw new Error(`HTTP ${response.status}`);

      this.apply(rows, bucket);
      if (bulk) this.clearSelection();
    } catch (_e) {
      showToast("Couldn't update the triage", "error");
    } finally {
      this.busy = false;
    }
  }

  // Each written row takes the bucket. One that no longer belongs on this tab fades out, and its
  // count moves to the tab it went to.
  private apply(rows: HTMLTableRowElement[], bucket: string): void {
    rows.forEach((row) => {
      const from = row.dataset.bucket || DEFAULT_BUCKET;
      row.dataset.bucket = bucket;
      this.paintThumbs(row, bucket);
      if (from === bucket) return;

      this.bumpCount(from, -1);
      this.bumpCount(bucket, 1);
      if (bucket !== this.bucketValue) this.leave(row);
    });
  }

  private paintThumbs(row: HTMLTableRowElement, bucket: string): void {
    row.querySelectorAll<HTMLElement>("[data-tri]").forEach((thumb) => {
      const key = thumb.dataset.tri || "";
      const on = key === bucket;
      thumb.classList.toggle("on", on);
      thumb.setAttribute("aria-pressed", String(on));
      thumb.title = on ? `${THUMB_LABELS[key]} — press to clear` : THUMB_LABELS[key];
    });
  }

  private bumpCount(bucket: string, delta: number): void {
    const badge = this.countTargets.find((el) => el.dataset.bucket === bucket);
    if (badge) {
      const count = Math.max(0, Number(badge.dataset.count || 0) + delta);
      badge.dataset.count = String(count);
      badge.textContent = count.toLocaleString("en-US");
    }

    // The current tab's size: the toolbar, the page header, the To-do focus chip and the pager.
    if (bucket !== this.bucketValue) return;
    document.querySelectorAll<HTMLElement>("[data-tab-total]").forEach((el) => {
      const count = Math.max(0, parseCount(el.textContent) + delta);
      const noun = el.dataset.noun;
      el.textContent = noun ? `${formatCount(count)} ${count === 1 ? noun : `${noun}s`}` : formatCount(count);
    });
  }

  // The rows left on this page: the header's "N of … loaded" and the pager's "from–to".
  private syncPageRange(): void {
    const shown = this.rows().length;

    document.querySelectorAll<HTMLElement>("[data-page-shown]").forEach((el) => {
      el.textContent = formatCount(Number(el.dataset.offset || 0) + shown);
    });

    document.querySelectorAll<HTMLElement>(".pager-info").forEach((info) => {
      const offset = Number(info.dataset.offset || 0);
      const from = info.querySelector<HTMLElement>("[data-page-from]");
      const to = info.querySelector<HTMLElement>("[data-page-to]");
      if (from) from.textContent = String(shown ? offset + 1 : 0);
      if (to) to.textContent = String(offset + shown);
    });
  }

  private leave(row: HTMLTableRowElement): void {
    this.setSelected(row, false);
    row.classList.add("is-leaving");

    window.setTimeout(() => {
      row.remove();
      this.syncSelection();
      this.syncPageRange();
      if (this.hasEmptyTarget && this.rows().length === 0) this.emptyTarget.hidden = false;
    }, LEAVE_MS);
  }
}
